import { Component, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { NormalizedQuery, PagedSearch, PageResult } from '@synegen/search';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { TableModule } from 'primeng/table';
import { Auth } from '@synegen/utility';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { Paginator } from 'primeng/paginator';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonDirective, ButtonIcon, ButtonLabel } from 'primeng/button';
import { BytesPipe } from '../../../pipes/bytes';
import { DatePipe } from '@angular/common';
import { Dialog } from 'primeng/dialog';
import { FileUploadComponent } from '../../../pages/file-upload/file-upload';
import { FileResponse } from '../../../types/data-lake.types';
import { FileApiService } from '../../../services/file-api.service';
import { MessageService } from 'primeng/api';
import { environment } from '../../../../environments/environment';
import { AI_TYPES } from '../../../pages/file-upload/file-upload.types';

@Component({
  selector: 'ems-file-search',
  imports: [
    TableModule,
    IconField,
    InputIcon,
    InputText,
    Paginator,
    TranslatePipe,
    ButtonDirective,
    ButtonIcon,
    ButtonLabel,
    BytesPipe,
    DatePipe,
    Dialog,
    FileUploadComponent
  ],
  templateUrl: './file-search.html',
  styleUrl: './file-search.scss',
  standalone: true
})
export class FileSearch extends PagedSearch<FileResponse>
  implements OnInit {
  #http: HttpClient = inject(HttpClient);
  #auth: Auth = inject(Auth);

  #fileApi: FileApiService = inject(FileApiService);
  #toast: MessageService = inject(MessageService);

  canCreate: WritableSignal<boolean> = signal(this.#auth.hasAnyAuthority('ems:datalake:upload'));
  // TODO: This conflicts with the canDelete of PagedSearch post 1.3.2 -> They should likely be combined
  localCanDelete: WritableSignal<boolean> = signal(this.#auth.hasAnyAuthority('ems:datalake:delete'));

  uploadFormVisible: WritableSignal<boolean> = signal(false);

  ngOnInit(): void {
    this.forceReload();
  }

  protected download(id: number): void {
    const a = document.createElement('a');
    a.href = `${environment.apiContext}/file/${id}/download`;
    a.click();
  }

  protected openUploadForm(): void {
    this.uploadFormVisible.set(true);
  }

  delete = (id: number): void => {
    this.#fileApi.deleteFile(id).subscribe({
      next: () => {
        this.forceReload();
        this.#toast.add({
          severity: 'success',
          summary: 'File was deleted successfully'
        });
      },

      error: (err) => {
        this.#toast.add({
          severity: 'error',
          summary: `File could not be deleted: ${err.error.message}`
        });
        console.error('Failed to delete file', err);
      }
    });
  };

  onSuccessfulUpload = (event: number) => {
    this.forceReload();
    this.uploadFormVisible.set(false);
  };

  protected override fetch(q: NormalizedQuery): Observable<PageResult<FileResponse>> {
    const body: NormalizedQuery = {
      ...q,
      sortKey: q.sortKey ?? 'id',
      sortDirection: q.sortDirection ?? 'asc',
      options: {
        ...(q.options ?? {}),
        bucket: 'datapool'
      }
    };

    return this.#http.post<PageResult<FileResponse>>('file/search?include=creationAudit', body);
  }

  protected readonly AI_TYPES = AI_TYPES;
}
